import React, { useEffect, useState } from "react";
import api, { formatErr, API } from "@/lib/api";
import { Plus, Loader2, X, FileText, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Émise", "Payée", "Relance"];
const STATUS_CLS = {
    "Émise": "chip-emise",
    "Payée": "chip-payee",
    "Relance": "chip-relance",
};

export default function InvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [ots, setOts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [modal, setModal] = useState(false);
    const [newInv, setNewInv] = useState({ ot_id: "", tva_rate: 20, notes: "", lines: [{ description: "", quantity: 1, unit_price: 0 }] });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: invs }, { data: otList }] = await Promise.all([
                api.get("/invoices", { params: filterStatus ? { status: filterStatus } : {} }),
                api.get("/ot"),
            ]);
            setInvoices(invs);
            setOts(otList);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

    const openCreate = () => {
        setNewInv({ ot_id: "", tva_rate: 20, notes: "", lines: [{ description: "", quantity: 1, unit_price: 0 }] });
        setModal(true);
    };

    const onOtSelect = (otId) => {
        const ot = ots.find((o) => o.id === otId);
        setNewInv((n) => ({
            ...n,
            ot_id: otId,
            lines: ot
                ? [{ description: `Prestation transport ${ot.reference} - ${ot.pickup_city} → ${ot.delivery_city}`, quantity: 1, unit_price: ot.price_ht || 0 }]
                : n.lines,
        }));
    };

    const create = async (e) => {
        e.preventDefault();
        if (!newInv.ot_id) { toast.error("Choisissez un OT"); return; }
        setSaving(true);
        try {
            const tvaNum = Number(newInv.tva_rate);
            const payload = {
                ot_id: newInv.ot_id,
                tva_rate: Number.isFinite(tvaNum) ? tvaNum : 20,
                notes: newInv.notes,
                lines: newInv.lines.map((l) => {
                    const q = Number(l.quantity);
                    const p = Number(l.unit_price);
                    return {
                        description: l.description,
                        quantity: Number.isFinite(q) ? q : 1,
                        unit_price: Number.isFinite(p) ? p : 0,
                    };
                }),
            };
            await api.post("/invoices", payload);
            toast.success("Facture créée");
            setModal(false);
            await load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const changeStatus = async (id, status) => {
        try {
            await api.patch(`/invoices/${id}/status`, { status });
            toast.success(`Facture ${status}`);
            await load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const remove = async (id, number) => {
        if (!confirm(`Supprimer la facture ${number} ?`)) return;
        await api.delete(`/invoices/${id}`);
        toast.success("Facture supprimée");
        await load();
    };

    const addLine = () => setNewInv((n) => ({ ...n, lines: [...n.lines, { description: "", quantity: 1, unit_price: 0 }] }));
    const updLine = (i, key, v) => setNewInv((n) => ({
        ...n, lines: n.lines.map((l, idx) => idx === i ? { ...l, [key]: v } : l),
    }));
    const rmLine = (i) => setNewInv((n) => ({ ...n, lines: n.lines.filter((_, idx) => idx !== i) }));

    const totals = (() => {
        const sub = newInv.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
        const tva = sub * (Number(newInv.tva_rate) || 0) / 100;
        return { sub, tva, total: sub + tva };
    })();

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-7xl mx-auto">
            <header className="flex flex-wrap items-end justify-between gap-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">Comptabilité</div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Facturation</h1>
                    <p className="text-sm text-slate-400 mt-1">Factures liées aux ordres de transport. Export PDF intégré.</p>
                </div>
                <button onClick={openCreate} data-testid="tms-create-invoice-button" className="kz-btn-primary text-sm"><Plus size={16} /> Nouvelle facture</button>
            </header>

            <div className="mt-6 flex gap-3 flex-wrap">
                <select
                    data-testid="invoice-filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="py-2.5 px-3 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white"
                >
                    <option value="">Tous statuts</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-800 bg-[#0f172a] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[800px]">
                        <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 font-medium">N°</th>
                                <th className="text-left px-5 py-3 font-medium">OT</th>
                                <th className="text-left px-5 py-3 font-medium">Client</th>
                                <th className="text-left px-5 py-3 font-medium">Émise le</th>
                                <th className="text-left px-5 py-3 font-medium">Échéance</th>
                                <th className="text-right px-5 py-3 font-medium">TTC</th>
                                <th className="text-left px-5 py-3 font-medium">Statut</th>
                                <th className="text-right px-5 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" />Chargement…</td></tr>}
                            {!loading && invoices.map((inv) => (
                                <tr key={inv.id} data-testid={`invoice-row-${inv.number}`} className="hover:bg-slate-800/40">
                                    <td className="px-5 py-3 font-mono text-slate-200">{inv.number}</td>
                                    <td className="px-5 py-3 font-mono text-slate-400">{inv.ot_reference}</td>
                                    <td className="px-5 py-3 text-slate-200">{inv.client_name}</td>
                                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{inv.issue_date}</td>
                                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{inv.due_date}</td>
                                    <td className="px-5 py-3 text-right text-slate-100 font-mono">{inv.total_ttc?.toLocaleString("fr-FR")} €</td>
                                    <td className="px-5 py-3">
                                        <select
                                            value={inv.status}
                                            onChange={(e) => changeStatus(inv.id, e.target.value)}
                                            className={`chip ${STATUS_CLS[inv.status]} bg-transparent cursor-pointer`}
                                        >
                                            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0f172a] text-white">{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {["fr", "en"].map((lg) => (
                                                <a
                                                    key={lg}
                                                    data-testid={`invoice-pdf-${lg}-${inv.number}`}
                                                    href={`${API}/invoices/${inv.id}/pdf?lang=${lg}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title={lg === "fr" ? "Télécharger en français" : "Télécharger en anglais"}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-500/15 border border-orange-500/30 text-orange-300 hover:bg-orange-500/25 text-xs"
                                                >
                                                    <Download size={12} /> PDF {lg.toUpperCase()}
                                                </a>
                                            ))}
                                            <button onClick={() => remove(inv.id, inv.number)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && invoices.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Aucune facture.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-5 overflow-y-auto">
                    <div className="bg-[#0f172a] rounded-2xl border border-slate-800 w-full max-w-3xl mt-8">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                            <h3 className="font-display font-semibold text-white flex items-center gap-2"><FileText size={16} /> Nouvelle facture</h3>
                            <button onClick={() => setModal(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                        </div>
                        <form onSubmit={create} className="p-6 space-y-5">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-slate-400 mb-1.5 block">OT associé *</label>
                                    <select required value={newInv.ot_id} onChange={(e) => onOtSelect(e.target.value)}
                                        className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white">
                                        <option value="">— Choisir un OT —</option>
                                        {ots.map((o) => <option key={o.id} value={o.id}>{o.reference} · {o.client_name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 mb-1.5 block">Taux TVA (%)</label>
                                    <input type="number" value={newInv.tva_rate} onChange={(e) => setNewInv({ ...newInv, tva_rate: e.target.value })}
                                        className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white" />
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs uppercase tracking-widest text-slate-400">Lignes</label>
                                    <button type="button" onClick={addLine} className="text-xs text-orange-300 hover:text-orange-200">+ Ajouter</button>
                                </div>
                                <div className="space-y-2">
                                    {newInv.lines.map((l, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2">
                                            <input value={l.description} onChange={(e) => updLine(i, "description", e.target.value)} placeholder="Description"
                                                className="col-span-7 py-2 px-3 rounded-lg bg-[#020617] border border-slate-700 text-sm text-white" />
                                            <input type="number" value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} placeholder="Qté"
                                                className="col-span-2 py-2 px-3 rounded-lg bg-[#020617] border border-slate-700 text-sm text-white" />
                                            <input type="number" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} placeholder="PU"
                                                className="col-span-2 py-2 px-3 rounded-lg bg-[#020617] border border-slate-700 text-sm text-white" />
                                            <button type="button" onClick={() => rmLine(i)} className="col-span-1 text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-800 text-sm">
                                <div><div className="text-xs text-slate-500">Sous-total HT</div><div className="font-mono text-slate-200 mt-0.5">{totals.sub.toFixed(2)} €</div></div>
                                <div><div className="text-xs text-slate-500">TVA</div><div className="font-mono text-slate-200 mt-0.5">{totals.tva.toFixed(2)} €</div></div>
                                <div><div className="text-xs text-slate-500">Total TTC</div><div className="font-mono text-orange-300 mt-0.5 font-semibold">{totals.total.toFixed(2)} €</div></div>
                            </div>

                            <div>
                                <label className="text-xs text-slate-400 mb-1.5 block">Notes</label>
                                <textarea rows={2} value={newInv.notes} onChange={(e) => setNewInv({ ...newInv, notes: e.target.value })}
                                    className="w-full py-2 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white" />
                            </div>

                            <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
                                <button type="button" onClick={() => setModal(false)} className="kz-btn-ghost text-sm">Annuler</button>
                                <button type="submit" disabled={saving} data-testid="invoice-form-submit" className="kz-btn-primary text-sm">
                                    {saving && <Loader2 className="animate-spin" size={14} />} Créer la facture
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
